import { useTranslation } from "react-i18next";
import {
  isRequestingContact,
  isRequestingPhoneAccess,
  requestContactComplete,
  requestContactPromise,
  requestPhoneAccess,
  requestPhoneAccessPromise,
} from "@telegram-apps/sdk-react";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { getApiErrorMessage } from "@/shared/utils/getApiErrorMessage.ts";
import { useSetPhone } from "./index.ts";

interface UseSharePhoneOptions {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

interface UseSharePhoneResult {
  sharePhone: () => Promise<void>;
  isPending: boolean;
}

const isDismissError = (e: any) => {
  const name = e?.name || e?.constructor?.name || "";
  return (
    name === "AccessDeniedError" ||
    name === "AbortError" ||
    name === "CancelError"
  );
};

export const useSharePhone = (
  options?: UseSharePhoneOptions
): UseSharePhoneResult => {
  const { t } = useTranslation();
  const { mutate: setPhone, isPending: isSettingPhone } = useSetPhone();

  const sharePhone = async () => {
    if (
      !requestPhoneAccess.isAvailable ||
      !requestPhoneAccess.isAvailable() ||
      !requestContactComplete.isAvailable ||
      !requestContactComplete.isAvailable()
    ) {
      showCustomToast(
        t("phone_share_unsupported") ||
          "Telegram versiyasi telefon ulashishni qo'llamaydi",
        "warning"
      );
      return;
    }

    // Abort any stale in-flight prompts so we don't trip
    // ConcurrentCallError when relaunching.
    if (isRequestingPhoneAccess()) {
      try {
        requestPhoneAccessPromise()?.abort?.();
      } catch {
        /* ignore */
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    if (isRequestingContact()) {
      try {
        requestContactPromise()?.abort?.();
      } catch {
        /* ignore */
      }
      await new Promise((r) => setTimeout(r, 50));
    }

    // Step 1 — always show the Telegram consent dialog. Unlike
    // requestContactComplete (which silently returns the cached contact if
    // the user has previously shared), requestPhoneAccess prompts every
    // time, giving the user the chance to confirm/deny again.
    let status: string;
    try {
      status = await requestPhoneAccess();
    } catch (e: any) {
      if (isDismissError(e)) return;
      console.error("requestPhoneAccess failed:", e);
      showCustomToast(
        getApiErrorMessage(
          e,
          t("phone_share_failed") || "Telefonni olib bo'lmadi"
        ),
        "error"
      );
      return;
    }
    if (status !== "sent") {
      // User dismissed or denied — silently no-op.
      return;
    }

    // Step 2 — now the contact is fresh in Telegram's cache. Pull the
    // signed payload (raw + parsed) so we can forward the HMAC-signed
    // raw string to the backend.
    let result: {
      raw: string;
      parsed: { contact?: { phone_number?: string } };
    };
    try {
      result = await requestContactComplete();
    } catch (e: any) {
      if (isDismissError(e)) return;
      console.error("requestContactComplete failed:", e);
      showCustomToast(
        getApiErrorMessage(
          e,
          t("phone_share_failed") || "Telefonni olib bo'lmadi"
        ),
        "error"
      );
      return;
    }

    if (!result?.raw || !result?.parsed?.contact?.phone_number) {
      return;
    }

    // Step 3 — send the raw HMAC-signed payload. Backend verifies the
    // hash and extracts the phone from the Telegram-attested contact.
    // No client-supplied phone is trusted.
    setPhone(result.raw, {
      onSuccess: () => {
        showCustomToast(
          t("phone_saved") || "Telefon raqam saqlandi",
          "success"
        );
        options?.onSuccess?.();
      },
      onError: (err: any) => {
        const message = getApiErrorMessage(
          err,
          t("phone_save_failed") || "Telefon saqlanmadi"
        );
        showCustomToast(message, "error");
        options?.onError?.(err);
      },
    });
  };

  return { sharePhone, isPending: isSettingPhone };
};
